import type { PropsWithChildren } from 'react';
import { Fragment, useId } from 'react';
import { styled } from 'styled-components';

import { unitMapping, type Unit } from '#src/lib/style.ts';

const RelativeRoot = styled.div`
  position: relative;
`;

type Props = {
  size?: Unit;
  progresses: number[];
  colors?: { from: string; to?: string }[];
  backgroundColor?: string;
};

const FULL_PROGRESS_WIDTH = 164;
const SPACE_BETWEEN_PROGRESSES = 10;

export const HalfCircularProgressBar = ({
  size = 8,
  progresses,
  colors = [{ from: 'white' }],
  backgroundColor = 'black',
}: PropsWithChildren<Props>) => {
  const linearGradientPrefix = useId();
  const N = progresses.length;

  return (
    <RelativeRoot>
      <svg
        viewBox="0 0 100 100"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        style={{
          position: 'relative',
          width: unitMapping[size],
        }}
      >
        <circle cx="50" cy="50" r="50" fill={backgroundColor} />
        {progresses.map((progress, i) => {
          const barWidth =
            (FULL_PROGRESS_WIDTH - (N - 1) * SPACE_BETWEEN_PROGRESSES) / N;

          const offsetValue = i * (barWidth + SPACE_BETWEEN_PROGRESSES);
          const offsetStr = `0 ${offsetValue}`;
          const barProgress = barWidth * progress;
          const dashOffset = 1;
          const dashArray = `${offsetStr} ${barWidth} ${FULL_PROGRESS_WIDTH - offsetValue - barWidth + dashOffset}`;
          const dashArrayProgress = `${offsetStr} ${barProgress} ${FULL_PROGRESS_WIDTH - offsetValue - barProgress + dashOffset}`;

          return (
            // oxlint-disable-next-line react/no-array-index-key
            <Fragment key={i}>
              <path
                d="M 5 60 A 46 46 0 1 1 95 60"
                stroke="white"
                strokeOpacity="0.16"
                strokeWidth="4"
                strokeLinecap="round"
                strokeDasharray={dashArray}
                strokeDashoffset={dashOffset}
              />
              <path
                stroke={`url(#${linearGradientPrefix}${i})`}
                d="M 5 60 A 46 46 0 1 1 95 60"
                strokeWidth="4"
                strokeLinecap={barProgress > 0 ? 'round' : undefined}
                strokeDasharray={dashArrayProgress}
                strokeDashoffset={dashOffset}
              />
            </Fragment>
          );
        })}
        <defs>
          {colors.map(({ from, to }, i) => (
            <linearGradient
              // oxlint-disable-next-line react/no-array-index-key
              key={i}
              id={`${linearGradientPrefix}${i}`}
              x1="62"
              y1="31.0775"
              x2="8.35651"
              y2="9.90504"
              gradientUnits="userSpaceOnUse"
            >
              <stop stopColor={from} />
              <stop offset="1" stopColor={to || from} />
            </linearGradient>
          ))}
        </defs>
      </svg>
    </RelativeRoot>
  );
};
